import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "cardholderName",
    "errors",
    "expiryMonth",
    "expiryYear",
    "number",
    "securityCode",
    "submitButton"
  ]

  static values = {
    purpose: String,
    redirectUrl: String,
    sessionUrl: String,
    submitUrl: String
  }

  async connect() {
    try {
      this.captureContext = await this.createCaptureContext()
      const library = this.captureContextLibrary(this.captureContext)
      await this.loadLibrary(library.url, library.integrity)
      this.mountMicroform()
      this.submitButtonTarget.disabled = false
    } catch (error) {
      this.showError(error.message || "Secure card entry could not be loaded. Please try again.")
    }
  }

  async submit(event) {
    event.preventDefault()
    this.hideError()

    if (!event.currentTarget.reportValidity()) return

    this.submitButtonTarget.disabled = true
    let requestSent = false

    try {
      const transientToken = await this.createTransientToken()
      requestSent = true
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: this.jsonHeaders(),
        body: JSON.stringify({
          transient_token: transientToken,
          cardholder_name: this.cardholderNameTarget.value
        })
      })
      const body = await response.json()

      if (!response.ok) {
        const error = new Error(body.errors || "The card could not be saved.")
        error.retryAllowed = body.retry_allowed !== false
        error.retryMessage = body.retry_message
        throw error
      }

      window.location.assign(body.redirect_url || this.redirectUrlValue)
    } catch (error) {
      if (requestSent && error.retryAllowed === undefined) {
        error.retryAllowed = false

        if (this.purposeValue === "payment") {
          error.message = "The payment outcome could not be confirmed. Please do not retry; contact support."
        } else {
          error.message = "The card update outcome could not be confirmed."
          error.retryMessage = "Reload this page before trying again."
        }
      }

      const retryMessage = error.retryMessage ? ` ${error.retryMessage}` : ""
      this.showError((error.message || "The card could not be saved. Please try again.") + retryMessage)
      if (error.retryAllowed !== false) this.submitButtonTarget.disabled = false
    }
  }

  async createCaptureContext() {
    const response = await fetch(this.sessionUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: this.jsonHeaders()
    })
    const body = await response.json()

    if (!response.ok) throw new Error(body.errors || "Secure card entry could not be started.")

    return body.capture_context
  }

  captureContextLibrary(captureContext) {
    const payload = JSON.parse(this.decodeBase64Url(captureContext.split(".")[1]))
    const data = payload.ctx?.[0]?.data

    if (!data?.clientLibrary || !data?.clientLibraryIntegrity) {
      throw new Error("NAB returned an invalid secure card configuration.")
    }

    return { url: data.clientLibrary, integrity: data.clientLibraryIntegrity }
  }

  loadLibrary(url, integrity) {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${url}"]`)
      if (existingScript?.dataset.loaded === "true") return resolve()

      const script = existingScript || document.createElement("script")
      script.src = url
      script.type = "text/javascript"
      script.async = true
      script.integrity = integrity
      script.crossOrigin = "anonymous"
      script.addEventListener("load", () => {
        script.dataset.loaded = "true"
        resolve()
      }, { once: true })
      script.addEventListener("error", () => reject(new Error("Secure card entry could not be loaded.")), { once: true })

      if (!existingScript) document.head.appendChild(script)
    })
  }

  mountMicroform() {
    const flex = new window.Flex(this.captureContext)
    this.microform = flex.microform("card", {
      styles: {
        input: {
          "font-family": "Roboto, Helvetica, Arial, sans-serif",
          "font-size": "14px",
          "font-weight": "400",
          "line-height": "20px",
          color: "#3c4858"
        },
        "::placeholder": { color: "#98a2b3" },
        ":focus": { color: "#202b3c" },
        invalid: { color: "#dc3545" }
      }
    })

    this.microform.createField("number", { placeholder: "Card number" }).load(this.numberTarget)

    if (this.purposeValue === "payment") {
      this.securityCodeValid = false
      this.securityCodeField = this.microform.createField("securityCode", { placeholder: "CVV", maxLength: 4 })
      // NAB exposes validation state, never the security-code value.
      this.securityCodeField.on("change", ({ valid, empty }) => {
        this.securityCodeValid = valid === true && empty === false
      })
      this.securityCodeField.load(this.securityCodeTarget)
    }
  }

  createTransientToken() {
    return new Promise((resolve, reject) => {
      if (this.purposeValue === "payment" && !this.securityCodeValid) {
        this.securityCodeField?.focus()
        return reject(new Error("Please enter a valid security code (CVV)."))
      }

      this.microform.createToken({
        expirationMonth: this.expiryMonthTarget.value,
        expirationYear: this.expiryYearTarget.value
      }, (error, token) => {
        if (error) return reject(new Error(error.message || "Please check the card details."))
        resolve(token)
      })
    })
  }

  jsonHeaders() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
    }
  }

  decodeBase64Url(value) {
    const padding = "=".repeat((4 - value.length % 4) % 4)
    return decodeURIComponent(Array.from(atob((value + padding).replace(/-/g, "+").replace(/_/g, "/")))
      .map(character => `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join(""))
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.errorsTarget.classList.remove("d-none")
  }

  hideError() {
    this.errorsTarget.textContent = ""
    this.errorsTarget.classList.add("d-none")
  }
}
